/* oxlint-disable anti-slop/no-runtime-typeof */
/**
 * PricingPage.js
 *
 * Data and actions for /pricing: the checkout catalog, billing period, plan
 * clicks and analytics. PricingPageExperiment.tsx draws the page.
 *
 * Rob ended the pricing page test on 2026-10-22 (PostHog experiment 383736,
 * flag pricing-page-redesign-2026-07) and kept Martin's redesign for every
 * visitor. The old "Choose Your Trading Power" layout, its flag read and its
 * ?pricingDesign= preview are gone. History: docs/experiments/2026-07-14_pricing-page-redesign.md.
 */
import useNextLiveCohortStart from '../hooks/useNextLiveCohortStart';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PrerenderReady from '../components/PrerenderReady';
import { Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import useUser from '../hooks/useUser';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import useCheckoutRedirect from '../hooks/useCheckoutRedirect';
import { navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';
import PricingPageExperiment from './PricingPageExperiment';
import { buildEducationPricingPlans } from './pricingEducationPlans';
import {
  BUSINESS_PRICING_PLAN as BUSINESS_PLAN,
  EDUCATION_PRICING_PLANS as EDUCATION_PLANS,
  PUBLIC_PRICING_TIERS as PRICING_TIERS,
  formatPublicPrice as formatPrice,
  applyCheckoutCatalogToPricingTiers,
  checkoutCatalogSupportsBillingPeriod,
} from '../site/publicPricingSource';
import './PricingPage.css';

const DIRECT_CHECKOUT_BUTTON_FEEDBACK_MS = 350;

function waitForButtonFeedbackPaint() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(resolve);
      return;
    }
    setTimeout(resolve, 0);
  });
}

async function waitForDirectCheckoutButtonFeedback() {
  await new Promise((resolve) => setTimeout(resolve, DIRECT_CHECKOUT_BUTTON_FEEDBACK_MS));
}

function BillingPeriodToggle({ billingPeriod, onChange }) {
  return (
    <div className="pricing-billing-toggle" role="group" aria-label="Billing period">
      <button
        type="button"
        aria-pressed={billingPeriod === 'monthly'}
        onClick={() => onChange('monthly')}
      >
        Monthly
      </button>
      <button
        type="button"
        aria-pressed={billingPeriod === 'annual'}
        onClick={() => onChange('annual')}
      >
        <span>Yearly</span>
        <small>2 months free</small>
      </button>
    </div>
  );
}

export default function PricingPage() {
  const { isAuthenticated, user } = useUser();
  const navigate = useNavigate();
  const checkout = useCheckoutRedirect('pricing_page');

  return (
    <PricingPageContent
      checkout={checkout}
      currentUser={user}
      isAuthenticated={isAuthenticated}
      navigate={navigate}
      trackEvent={pushEvent}
    />
  );
}

export function PricingPageContent({
  checkout,
  currentUser,
  isAuthenticated,
  navigate,
  trackEvent,
}) {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedBillingPeriod = queryParams.get('billing') === 'annual' ? 'annual' : 'monthly';
  const [pendingPlanId, setPendingPlanId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState(requestedBillingPeriod);

  const { startCheckout, catalog, catalogLoading, catalogError } = checkout;

  // Light is always visible on the pricing page. It's a real product, not an A/B-gated teaser.
  const coreTiers = useMemo(
    () =>
      PRICING_TIERS.filter((tier) => ['light', 'starter', 'pro', 'all-access'].includes(tier.id)),
    [],
  );
  const annualBillingAvailable = useMemo(
    () => Boolean(catalog && checkoutCatalogSupportsBillingPeriod(coreTiers, catalog, 'annual')),
    [catalog, coreTiers],
  );
  const effectiveBillingPeriod =
    billingPeriod === 'annual' && annualBillingAvailable ? 'annual' : 'monthly';
  const pricingCatalogProjection = useMemo(() => {
    if (!catalog) return { plans: coreTiers, error: null };
    try {
      return {
        plans: applyCheckoutCatalogToPricingTiers(coreTiers, catalog, effectiveBillingPeriod),
        error: null,
      };
    } catch (error) {
      return { plans: [], error };
    }
  }, [catalog, coreTiers, effectiveBillingPeriod]);
  const CORE_PLANS = pricingCatalogProjection.plans;
  // Bootcamp prices, payment plans and facts come only from the catalog. A
  // Bootcamp catalog problem hides the Bootcamp buy buttons but must not take
  // the subscription plans down with it.
  // A closed live row says when the next cohort starts, from session rows.
  const nextLiveStart = useNextLiveCohortStart();
  const educationProjection = useMemo(() => {
    if (!catalog) return { plans: [], error: null };
    try {
      return {
        plans: buildEducationPricingPlans(catalog, EDUCATION_PLANS, { nextLiveStart }),
        error: null,
      };
    } catch (error) {
      console.error('[PricingPage] Bootcamp pricing unavailable', error);
      return { plans: [], error };
    }
  }, [catalog, nextLiveStart]);
  const educationPlans = educationProjection.plans;
  const educationUnavailable = Boolean(educationProjection.error);
  const pageReady = catalogLoading !== true;

  useEffect(() => {
    setBillingPeriod(requestedBillingPeriod);
  }, [requestedBillingPeriod]);

  useEffect(() => {
    if (!location.hash || !pageReady) return undefined;

    const targetId = decodeURIComponent(location.hash.slice(1));
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const targetTop = window.scrollY + target.getBoundingClientRect().top - 96;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, pageReady]);

  // NOTE: /pricing shows the currently active public discounted offer. The
  // Light-vs-Starter A/B test still runs only on generic sales/marketplace
  // /checkout/starter-plan entries with no explicit product selection.

  // Track pricing page view
  useEffect(() => {
    trackEvent({
      event: 'PricingPageView',
      source: 'pricing_page',
    });
  }, [trackEvent]);

  const handleBillingPeriodChange = useCallback(
    (nextBillingPeriod) => {
      if (nextBillingPeriod === effectiveBillingPeriod) return;
      setPendingPlanId(null);
      setBillingPeriod(nextBillingPeriod);

      const nextQueryParams = new URLSearchParams(queryParams);
      if (nextBillingPeriod === 'annual') {
        nextQueryParams.set('billing', 'annual');
      } else {
        nextQueryParams.delete('billing');
      }
      navigate(
        {
          pathname: location.pathname,
          search: nextQueryParams.toString() ? `?${nextQueryParams.toString()}` : '',
          hash: location.hash,
        },
        { replace: true, state: location.state },
      );

      trackEvent({
        event: 'PricingBillingPeriodChanged',
        source: '/pricing',
        billing_period: nextBillingPeriod,
      });
    },
    [
      effectiveBillingPeriod,
      location.hash,
      location.pathname,
      location.state,
      navigate,
      queryParams,
      trackEvent,
    ],
  );

  const handlePlanClick = useCallback(
    async (tier) => {
      // Closed enrollment still permits course information, but cannot start checkout.
      if (!tier || pendingPlanId || (tier.checkoutClosedMessage && !tier.coursePath)) return;

      setPendingPlanId(tier.id);
      await waitForButtonFeedbackPaint();

      // Determine destination for analytics. Bootcamp rows open their course
      // page (research 2026-10-21: buyers read the course page before paying).
      const destination = tier.coursePath
        ? tier.coursePath
        : tier.useCartCheckout
          ? '/checkout'
          : tier.ctaLink || '/checkout';

      trackEvent({
        event: ANALYTICS_EVENTS.PRICING_CTA_CLICK,
        button_text: tier.cta,
        source: '/pricing',
        destination,
        plan: tier.id,
        price: tier.price,
        billing_period: tier.billingPeriod || effectiveBillingPeriod,
        plan_key: tier.planKey || null,
        user_type: isAuthenticated ? 'authenticated' : 'anonymous',
      });

      if (!tier.isExternal && destination.startsWith('/checkout')) {
        trackEvent({
          event: ANALYTICS_EVENTS.SUBSCRIPTION_CTA_CLICKED,
          source: '/pricing',
          current_path: `${location.pathname}${location.search || ''}`,
          action: 'subscribe',
          feature: tier.id,
          plan: tier.id,
          product_slug: tier.productSlug || null,
          plan_key: tier.planKey || null,
          billing_period: tier.billingPeriod || effectiveBillingPeriod,
          destination,
        });
      }

      try {
        if (tier.coursePath) {
          navigate(`${tier.coursePath}${location.search || ''}`);
          return;
        }

        // Handle external links (like Calendly for Business plan)
        if (tier.isExternal) {
          window.open(tier.ctaLink, '_blank', 'noopener,noreferrer');
          setPendingPlanId(null);
          return;
        }

        // Handle cart-based checkout (Pro, All Access)
        if (tier.useCartCheckout && tier.productSlug) {
          const origin = {
            flow: 'pricing_page',
            plan: tier.id,
            planKey: tier.planKey || null,
            billingPeriod: tier.billingPeriod || effectiveBillingPeriod,
          };
          await startCheckout({
            products: [{ productSlug: tier.productSlug, planKey: tier.planKey }],
            flowContext: 'pricing_page',
            metadata: { origin },
            contact: {
              email: currentUser?.email || '',
              name: currentUser?.name || '',
            },
          });
          return;
        }

        // Handle direct link navigation (Light and Starter plan pages)
        if (tier.ctaLink) {
          await waitForDirectCheckoutButtonFeedback();
          navigateToCheckoutOverlay(navigate, tier.ctaLink, location);
        } else {
          setPendingPlanId(null);
        }
      } catch (error) {
        setPendingPlanId(null);
        throw error;
      }
    },
    [
      isAuthenticated,
      effectiveBillingPeriod,
      currentUser?.email,
      currentUser?.name,
      location,
      navigate,
      pendingPlanId,
      startCheckout,
      trackEvent,
    ],
  );

  if (catalogLoading === true) {
    return (
      <div className="pricing-page pricing-page--catalog-state" role="status">
        <h1>Plans and pricing</h1>
        <p>
          <Spinner animation="border" size="sm" aria-hidden="true" /> Loading current pricing.
        </p>
      </div>
    );
  }

  if (catalogLoading === false && (catalogError || !catalog || pricingCatalogProjection.error)) {
    return (
      <div className="pricing-page pricing-page--catalog-state" role="alert">
        <h1>Plans and pricing</h1>
        <p>Current pricing is unavailable. Please refresh and try again.</p>
      </div>
    );
  }

  return (
    <>
      {/* The crawler snapshot is taken only from the live checkout catalog,
      never from the tier defaults this page renders before it loads. */}
      <PrerenderReady ready={Boolean(catalog)} state="catalog" />
      <PricingPageExperiment
        businessPlan={BUSINESS_PLAN}
        corePlans={CORE_PLANS}
        billingPeriod={effectiveBillingPeriod}
        billingPeriodControl={
          annualBillingAvailable ? (
            <BillingPeriodToggle
              billingPeriod={effectiveBillingPeriod}
              onChange={handleBillingPeriodChange}
            />
          ) : null
        }
        educationPlans={educationPlans}
        educationUnavailable={educationUnavailable}
        formatPrice={formatPrice}
        onPlanClick={handlePlanClick}
        pendingPlanId={pendingPlanId}
      />
    </>
  );
}
